import React, { useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { resolveVioAssetUrl } from '../../utils/vioAssetUrl';

function toFiniteNumber(value) {
  if (value == null) return NaN;
  const v = typeof value === 'string' ? value.trim() : value;
  if (v === '') return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

/** VIO: totalQuantity < 0 (default -1) means unlimited. Missing field = unlimited. */
function voucherIsUnlimitedStock(schema) {
  const rem = toFiniteNumber(schema.remainingQuantity);
  if (Number.isFinite(rem) && rem < 0) return true;
  const raw = schema.totalQuantity;
  if (raw == null || (typeof raw === 'string' && raw.trim() === '')) return true;
  const total = toFiniteNumber(typeof raw === 'string' ? raw.trim() : raw);
  if (!Number.isFinite(total)) return true;
  return total < 0;
}

function voucherStockDisplay(schema) {
  if (voucherIsUnlimitedStock(schema)) {
    return { text: 'Unlimited', color: '#16a34a' };
  }
  const total = toFiniteNumber(schema.totalQuantity);
  const cap = Number.isFinite(total) && total >= 0 ? total : 0;
  const claimed = toFiniteNumber(schema.claimedQuantity) || 0;
  let remaining;
  const remRaw = schema.remainingQuantity;
  if (remRaw != null && remRaw !== '') {
    const r = toFiniteNumber(remRaw);
    remaining = Number.isFinite(r) && r >= 0 ? r : Math.max(0, cap - claimed);
  } else {
    remaining = Math.max(0, cap - claimed);
  }
  if (remaining <= 0) {
    return { text: cap > 0 ? `0 of ${cap} — Sold out` : 'Sold out', color: '#ef4444' };
  }
  if (cap > 0 && remaining < cap) {
    return { text: `${remaining} of ${cap} available`, color: '#16a34a' };
  }
  return { text: `${remaining} available`, color: '#16a34a' };
}

function isVoucherExpired(schema) {
  if (!schema?.expiryEnd) return false;
  const expiryTs = new Date(schema.expiryEnd).getTime();
  return Number.isFinite(expiryTs) && expiryTs < Date.now();
}

function isVoucherOutOfStock(schema) {
  if (!schema?.isLimited) return false;
  const remaining = Number(schema?.remainingQuantity);
  return Number.isFinite(remaining) ? remaining <= 0 : false;
}

function getVoucherAlerts(voucher, voucherSchemas) {
  const alerts = [];
  const selectedSchema = voucherSchemas?.find((schema) => schema.id === voucher.schemaId);

  if (!selectedSchema) return alerts;

  if (isVoucherExpired(selectedSchema)) {
    alerts.push('This selected voucher has expired in VIO.');
  }

  if (isVoucherOutOfStock(selectedSchema)) {
    alerts.push('This selected voucher is out of stock in VIO.');
  }

  if (voucher.inventory && (voucher.issued || 0) >= Number(voucher.inventory)) {
    alerts.push('This selected voucher has exhausted its configured pool inventory.');
  }

  return alerts;
}

function getTokenAlerts(rule, tokenList) {
  const alerts = [];
  const selectedToken = tokenList?.find((token) => token.id === rule.tokenId);

  if (!selectedToken) return alerts;

  if (!selectedToken.balance || Number(selectedToken.balance) <= 0) {
    alerts.push('This selected token is out of stock in VIO.');
  }

  return alerts;
}

function renderAlerts(alerts) {
  if (!alerts?.length) return null;

  return (
    <div style={{
      marginTop: '0.5rem',
      padding: '0.625rem 0.75rem',
      borderRadius: '8px',
      background: '#fff7ed',
      border: '1px solid #fdba74',
      color: '#9a3412',
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    }}>
      {alerts.map((alert) => (
        <div key={alert}>{alert}</div>
      ))}
    </div>
  );
}

/** Canonical rewards as stored on server (same inference as sync `useEffect`). */
function rewardsFromConfig(config) {
  const r = config?.rewards;
  if (!r) return null;
  let types;
  if (r.types && r.types.length > 0) {
    types = r.types;
  } else if (r.tokenRules && r.tokenRules.length > 0) {
    types = ['TOKEN'];
  } else if (r.voucherPool && r.voucherPool.length > 0) {
    types = ['VOUCHER'];
  } else {
    types = ['VOUCHER'];
  }
  return {
    types,
    tokenRules: r.tokenRules || [],
    voucherPool: r.voucherPool || [],
    voucherRotationEnabled: r.voucherRotationEnabled !== false,
  };
}

/** Remaining issuable count (`remainingQuantity`, or cap − claimed). Undefined if unlimited / no schema. */
function voucherRemainingIssuableCount(schema) {
  if (!schema || voucherIsUnlimitedStock(schema)) return undefined;
  const total = toFiniteNumber(schema.totalQuantity);
  const cap = Number.isFinite(total) && total >= 0 ? total : 0;
  const claimed = toFiniteNumber(schema.claimedQuantity) || 0;
  let remaining;
  const remRaw = schema.remainingQuantity;
  if (remRaw != null && remRaw !== '') {
    const r = toFiniteNumber(remRaw);
    remaining = Number.isFinite(r) && r >= 0 ? r : Math.max(0, cap - claimed);
  } else {
    remaining = Math.max(0, cap - claimed);
  }
  return Number.isFinite(remaining) ? remaining : undefined;
}

/** Remaining count from VIO list mapping (for display). */
function voucherAvailableQuantityFromApi(schema) {
  if (!schema) return null;
  if (voucherIsUnlimitedStock(schema)) return 'Unlimited';
  const remaining = voucherRemainingIssuableCount(schema);
  return remaining != null ? remaining.toLocaleString() : null;
}

/** Pool cap from saved voucher row (`inventory`); empty means no limit configured in ops. */
function voucherConfiguredPoolAmountText(voucher) {
  if (voucher == null) return 'Unlimited';
  const raw = voucher.inventory;
  if (raw == null || raw === '') return 'Unlimited';
  const n = typeof raw === 'number' ? raw : parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || n <= 0) return 'Unlimited';
  return n.toLocaleString();
}

/** maxClaimsPerUser <= 0 means unlimited per user. null/undefined defaults to 1. */
function hasPerUserClaimLimit(schema) {
  const raw = schema?.maxClaimsPerUser;
  if (raw == null || raw === '') return true;
  const n = toFiniteNumber(raw);
  return !Number.isFinite(n) || n > 0;
}

function maxClaimsPerUserLabel(schema) {
  const raw = schema.maxClaimsPerUser;
  if (raw === undefined || raw === null || (typeof raw === 'string' && raw.trim() === '')) {
    return 'Max/User: 1';
  }
  const n = toFiniteNumber(raw);
  if (!Number.isFinite(n) || n <= 0) return 'Unlimited/User';
  return `Max/User: ${n}`;
}

function isVoucherSelectable(schema) {
  if (hasPerUserClaimLimit(schema)) return false;
  if (voucherIsUnlimitedStock(schema)) return true;
  const total = toFiniteNumber(schema.totalQuantity);
  const cap = Number.isFinite(total) && total >= 0 ? total : 0;
  const claimed = toFiniteNumber(schema.claimedQuantity) || 0;
  if (schema.remainingQuantity != null && schema.remainingQuantity !== '') {
    const r = toFiniteNumber(schema.remainingQuantity);
    if (Number.isFinite(r) && r >= 0) return r > 0;
  }
  return Math.max(0, cap - claimed) > 0;
}

/** VIO schema expiry (from RulesConfig mapping: expiryEnd) */
function voucherSchemaIsExpired(schema) {
  if (!schema?.expiryEnd) return false;
  const t = new Date(schema.expiryEnd).getTime();
  if (!Number.isFinite(t)) return false;
  return t < Date.now();
}

/** Campaign no longer active in VIO — cannot be issued as a reward (RulesConfig maps `isActive`). */
function voucherCampaignIsInactive(schema) {
  if (!schema) return false;
  return schema.isActive === false;
}

function safePositiveBalance(balance) {
  const n = toFiniteNumber(balance);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
}

function RewardSelectionAlertsVoucher({ schema }) {
  if (!schema) return null;
  const expired = voucherSchemaIsExpired(schema);
  const inactive = voucherCampaignIsInactive(schema);
  const blocked = !isVoucherSelectable(schema);
  const perUserBlock = blocked && hasPerUserClaimLimit(schema);
  const soldOut = blocked && !hasPerUserClaimLimit(schema);
  if (!expired && !inactive && !soldOut && !perUserBlock) return null;
  return (
    <div
      style={{
        marginTop: '0.35rem',
        padding: '0.5rem 0.75rem',
        borderRadius: '6px',
        background: '#fff7ed',
        border: '1px solid #fdba74',
        fontSize: '0.8125rem',
        color: '#9a3412',
        lineHeight: 1.45
      }}
    >
      {expired ? <div>Warning: This voucher has expired (VIO end date has passed).</div> : null}
      {inactive ? <div>Warning: This voucher's campaign is no longer active and cannot be issued as a reward.</div> : null}
      {soldOut ? <div>Warning: This voucher is out of stock (no remaining quantity).</div> : null}
      {perUserBlock ? (
        <div>Warning: This VIO schema limits claims per user and cannot be used in the reward pool.</div>
      ) : null}
    </div>
  );
}

function normId(v) {
  if (v == null) return '';
  return String(v).trim();
}

/** All identifiers a VIO row may be referenced by (pool `schemaId` can match any). */
function voucherSchemaIdentifiers(schema) {
  if (!schema) return [];
  return [schema.id, schema.schemaId, schema.campaignVoucherSchemaId]
    .filter((x) => x != null)
    .map(normId);
}

/**
 * True when this VIO schema is already used by a voucher in the pool.
 * `excludeRowId` skips the row currently being edited so it stays selectable.
 */
function isVoucherAlreadyInPool(schema, voucherPool, excludeRowId) {
  const ids = voucherSchemaIdentifiers(schema);
  if (ids.length === 0 || !Array.isArray(voucherPool)) return false;
  return voucherPool.some((v) => {
    if (!v) return false;
    if (excludeRowId != null && v.id === excludeRowId) return false;
    return ids.includes(normId(v.schemaId));
  });
}

/** Match pool `schemaId` to a loaded VIO row (id / campaign id can differ). */
function findVoucherSchema(voucherSchemas, poolSchemaId) {
  const n = normId(poolSchemaId);
  if (!n || !Array.isArray(voucherSchemas)) return null;
  return (
    voucherSchemas.find((s) => {
      const ids = [s.id, s.schemaId, s.campaignVoucherSchemaId]
        .filter((x) => x != null)
        .map(normId);
      return ids.includes(n);
    }) || null
  );
}

/**
 * Persist VIO list row label + image (same fields as GET /vio/vouchers → RulesConfig mapping)
 * so pool rows still show API-accurate text/media when the schema disappears from the published list.
 */
function vioSnapshotFields(schema) {
  if (!schema) return {};
  const name = schema.name != null && String(schema.name).trim() !== '' ? String(schema.name).trim() : undefined;
  const imageUrl = schema.imageUrl != null && String(schema.imageUrl).trim() !== '' ? String(schema.imageUrl).trim() : undefined;
  const out = {};
  if (name) out.vioDisplayName = name;
  if (imageUrl) out.vioImageUrl = imageUrl;
  return out;
}

function generateLocalId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

/** Match saved rule `tokenId` to list row (external vs miniapp id fields differ). */
function findTokenInList(tokenList, tokenId) {
  const n = normId(tokenId);
  if (!n || !Array.isArray(tokenList)) return null;
  return (
    tokenList.find((t) => {
      const ids = [t.id, t.dbTokId, t.mongoId].filter((x) => x != null).map(normId);
      return ids.includes(n);
    }) || null
  );
}

/** Company/treasury balance from VIO token list (`balance`); missing → unlimited (same as token picker). */
function tokenAvailableQuantityFromApi(token) {
  if (!token) return null;
  const bal = safePositiveBalance(token.balance);
  if (bal === undefined) return 'Unlimited';
  return bal.toLocaleString();
}

function RewardSelectionAlertsToken({ token }) {
  if (!token) return null;
  const bal = safePositiveBalance(token.balance);
  if (bal === undefined || bal > 0) return null;
  return (
    <div
      style={{
        marginTop: '0.35rem',
        padding: '0.5rem 0.75rem',
        borderRadius: '6px',
        background: '#fef2f2',
        border: '1px solid #fecaca',
        fontSize: '0.8125rem',
        color: '#991b1b',
        lineHeight: 1.45
      }}
    >
      Warning: Company token balance is zero — this token is out of stock until treasury balance is funded.
    </div>
  );
}

/** Pool cap reached for this configured row (independent of VIO schema load). */
function configuredVoucherPoolExhausted(voucher) {
  if (!voucher) return false;
  const raw = voucher.inventory;
  if (raw == null || raw === '') return false;
  const cap = typeof raw === 'number' ? raw : parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(cap) || cap <= 0) return false;
  const issued = voucher.issued || 0;
  return issued >= cap;
}

/**
 * Display state for a saved voucher pool row: grey row + badge when unusable in VIO / pool.
 * Badge: "Expired" | "Out of stock" | "Unavailable" (per-user schema limit).
 *
 * When the VIO published list no longer includes this schemaId (common for expired vouchers),
 * we still grey the row: the pool points at a reward that is not issuable from the list.
 */
function getConfiguredVoucherRowState(schema, voucher, voucherSchemas) {
  const poolExhausted = configuredVoucherPoolExhausted(voucher);
  const listLoaded = Array.isArray(voucherSchemas) && voucherSchemas.length > 0;
  const poolHasSchemaId = Boolean(normId(voucher?.schemaId));
  const absentFromPublishedList = !schema && listLoaded && poolHasSchemaId;

  if (!schema) {
    if (poolExhausted) return { muted: true, badge: 'Out of stock' };
    if (absentFromPublishedList) return { muted: true, badge: 'Expired' };
    return { muted: false, badge: null };
  }
  if (voucherSchemaIsExpired(schema)) {
    return { muted: true, badge: 'Expired' };
  }
  if (voucherCampaignIsInactive(schema)) {
    return { muted: true, badge: 'Inactive' };
  }
  if (poolExhausted) {
    return { muted: true, badge: 'Out of stock' };
  }
  if (isVoucherOutOfStock(schema)) {
    return { muted: true, badge: 'Out of stock' };
  }
  const blocked = !isVoucherSelectable(schema);
  if (blocked && !hasPerUserClaimLimit(schema)) {
    return { muted: true, badge: 'Out of stock' };
  }
  if (blocked && hasPerUserClaimLimit(schema)) {
    return { muted: true, badge: 'Unavailable' };
  }
  return { muted: false, badge: null };
}

/** Treasury balance zero (finite) → out of stock for token reward row. */
function getConfiguredTokenRowState(token) {
  const bal = token ? safePositiveBalance(token.balance) : undefined;
  if (bal === undefined || bal > 0) return { muted: false, badge: null };
  return { muted: true, badge: 'Out of stock' };
}

/** Voucher / token row thumbnail: show remote image when URL works, else gradient + fallback content */
function RewardListThumb({ imageUrl, alt, fallback, fallbackStyle, muted = false }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [imageUrl]);
  const resolved = imageUrl ? resolveVioAssetUrl(imageUrl) : null;
  const showImg = Boolean(resolved) && !broken;
  return (
    <div
      className={`item-icon${muted ? ' item-icon--muted' : ''}`}
      style={
        showImg
          ? {
              padding: 0,
              overflow: 'hidden',
              background: '#f1f5f9',
              border: '1px solid #e2e8f0',
              color: 'transparent'
            }
          : fallbackStyle
      }
    >
      {showImg ? (
        <img
          src={resolved}
          alt={alt || ''}
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          className="item-icon-img"
        />
      ) : (
        fallback
      )}
    </div>
  );
}

/** Compare persisted reward fields (types, rotation, pools) independent of array order / extra VIO fields */
function normalizeRewardSnapshot(r) {
  if (!r) r = {};
  const types = (Array.isArray(r.types) && r.types.length > 0 ? [...r.types] : ['VOUCHER']).sort();
  const pool = [...(r.voucherPool || [])]
    .map((v) => ({
      id: String(v.id ?? ''),
      name: String(v.name ?? '').trim(),
      schemaId: String(v.schemaId ?? '').trim(),
      inventory: v.inventory === '' || v.inventory == null ? '' : String(v.inventory),
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
  const tokenRules = [...(r.tokenRules || [])]
    .map((t) => ({
      id: String(t.id ?? ''),
      tokenId: String(t.tokenId ?? ''),
      tokenType: String(t.tokenType ?? '').trim(),
      spendAmount: String(t.spendAmount ?? '').trim(),
      earnAmount: String(t.earnAmount ?? '').trim(),
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify({
    types,
    voucherRotationEnabled: r.voucherRotationEnabled !== false,
    voucherPool: pool,
    tokenRules,
  });
}

const RewardsTab = forwardRef(function RewardsTab({ config, onUpdate, voucherSchemas, tokenList, hideWizardSaveBar = false }, ref) {
  const [formData, setFormData] = useState({
    types: ['VOUCHER'],
    tokenRules: [],
    voucherPool: [],
    voucherRotationEnabled: true
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [isAddingVoucher, setIsAddingVoucher] = useState(false);
  const [isAddingToken, setIsAddingToken] = useState(false);
  const [editingVoucherId, setEditingVoucherId] = useState(null);
  const [editingTokenId, setEditingTokenId] = useState(null);
  const [newVoucher, setNewVoucher] = useState({ name: '', schemaId: '', inventory: '' });
  const [newToken, setNewToken] = useState({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
  const [editingVoucher, setEditingVoucher] = useState({ name: '', schemaId: '', inventory: '' });
  const [editingToken, setEditingToken] = useState({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
  
  // Track if user has manually selected a reward type
  const userSelectedTypeRef = useRef(false);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const hasRewardInlineDraft = useMemo(
    () =>
      editingVoucherId != null ||
      editingTokenId != null ||
      (isAddingVoucher &&
        (newVoucher.name.trim() !== '' ||
          newVoucher.schemaId.trim() !== '' ||
          newVoucher.inventory !== '')) ||
      (isAddingToken &&
        (newToken.tokenType.trim() !== '' ||
          String(newToken.spendAmount || '').trim() !== '' ||
          String(newToken.earnAmount || '').trim() !== '' ||
          String(newToken.tokenId || '').trim() !== '')),
    [
      editingVoucherId,
      editingTokenId,
      isAddingVoucher,
      isAddingToken,
      newVoucher.name,
      newVoucher.schemaId,
      newVoucher.inventory,
      newToken.tokenType,
      newToken.spendAmount,
      newToken.earnAmount,
      newToken.tokenId,
    ]
  );

  const persistedRewardsDirty = useMemo(
    () => normalizeRewardSnapshot(formData) !== normalizeRewardSnapshot(config?.rewards),
    [formData, config?.rewards]
  );

  const isDirty = hasRewardInlineDraft || persistedRewardsDirty;

  useEffect(() => {
    if (config?.rewards) {
      // Preserve the current reward type selection when config updates
      // This prevents switching from TOKEN back to VOUCHER after adding/updating token rules
      setFormData(prev => {
        const newData = { ...config.rewards };
        // If user has manually selected a type during this session, preserve it
        if (userSelectedTypeRef.current && prev.types && prev.types.length > 0) {
          newData.types = prev.types;
        } else {
          // Use the saved types from config (this is what user saved)
          // Only fall back to inference if types is not set
          if (config.rewards.types && config.rewards.types.length > 0) {
            newData.types = config.rewards.types;
          } else if (config.rewards.tokenRules && config.rewards.tokenRules.length > 0) {
            newData.types = ['TOKEN'];
          } else if (config.rewards.voucherPool && config.rewards.voucherPool.length > 0) {
            newData.types = ['VOUCHER'];
          } else {
            newData.types = ['VOUCHER'];
          }
        }
        return newData;
      });
    }
  }, [config]);

  // 单选逻辑 - 只能选择一种奖励类型
  const handleTypeToggle = (type) => {
    userSelectedTypeRef.current = true;
    setFormData(prev => ({ ...prev, types: [type] }));
  };

  const buildRewardsForPersist = useCallback(() => {
    const base = { ...formDataRef.current };
    let pool = [...(base.voucherPool || [])];
    let rules = [...(base.tokenRules || [])];

    if (editingVoucherId) {
      if (!editingVoucher.name.trim() || !editingVoucher.schemaId.trim()) {
        return { error: 'Complete voucher edit (name and schema) or cancel before saving.' };
      }
      if (editingVoucher.inventory !== '' && parseInt(editingVoucher.inventory, 10) <= 0) {
        return { error: 'Voucher inventory must be greater than 0 when set.' };
      }
      const selW = findVoucherSchema(voucherSchemas, editingVoucher.schemaId);
      const editDuplicate = selW
        ? isVoucherAlreadyInPool(selW, pool, editingVoucherId)
        : pool.some((v) => v.id !== editingVoucherId && normId(v.schemaId) === normId(editingVoucher.schemaId));
      if (editDuplicate) {
        return { error: 'This voucher is already in the pool. Choose a different voucher.' };
      }
      if (voucherCampaignIsInactive(selW)) {
        return { error: "This voucher's campaign is no longer active and cannot be used as a reward." };
      }
      const invParsed = parseInt(editingVoucher.inventory, 10);
      const inv =
        editingVoucher.inventory !== '' && !Number.isNaN(invParsed) && invParsed > 0 ? invParsed : null;
      pool = pool.map((v) =>
        v.id === editingVoucherId
          ? {
              ...v,
              name: editingVoucher.name.trim(),
              schemaId: editingVoucher.schemaId.trim(),
              inventory: inv,
              ...vioSnapshotFields(selW)
            }
          : v
      );
    }

    if (editingTokenId) {
      const spendNum = parseFloat(editingToken.spendAmount);
      const earnNum = parseFloat(editingToken.earnAmount);
      if (!editingToken.tokenType.trim() || !spendNum || spendNum <= 0 || !earnNum || earnNum <= 0) {
        return { error: 'Complete token rule edit or cancel before saving.' };
      }
      rules = rules.map((r) =>
        r.id === editingTokenId
          ? {
              ...r,
              tokenId: editingToken.tokenId?.trim() || null,
              tokenType: editingToken.tokenType.trim(),
              spendAmount: spendNum,
              earnAmount: earnNum
            }
          : r
      );
    }

    if (isAddingVoucher) {
      const hasAny =
        newVoucher.name.trim() || newVoucher.schemaId.trim() || newVoucher.inventory !== '';
      if (newVoucher.name.trim() && newVoucher.schemaId.trim()) {
        if (newVoucher.inventory !== '' && parseInt(newVoucher.inventory, 10) <= 0) {
          return { error: 'New voucher inventory must be greater than 0 when set.' };
        }
        const selN = findVoucherSchema(voucherSchemas, newVoucher.schemaId);
        const addDuplicate = selN
          ? isVoucherAlreadyInPool(selN, pool, null)
          : pool.some((v) => normId(v.schemaId) === normId(newVoucher.schemaId));
        if (addDuplicate) {
          return { error: 'This voucher is already in the pool. Choose a different voucher.' };
        }
        if (voucherCampaignIsInactive(selN)) {
          return { error: "This voucher's campaign is no longer active and cannot be added as a reward." };
        }
        const invParsed = parseInt(newVoucher.inventory, 10);
        const inv =
          newVoucher.inventory !== '' && !Number.isNaN(invParsed) && invParsed > 0 ? invParsed : null;
        pool = [
          ...pool,
          {
            id: generateLocalId(),
            name: newVoucher.name.trim(),
            schemaId: newVoucher.schemaId.trim(),
            inventory: inv,
            issued: 0,
            order: pool.length + 1,
            createdAt: new Date().toISOString(),
            ...vioSnapshotFields(selN)
          }
        ];
      } else if (hasAny) {
        return { error: 'Complete new voucher (name and schema) or cancel before saving.' };
      }
    }

    if (isAddingToken) {
      const spendNum = parseFloat(newToken.spendAmount);
      const earnNum = parseFloat(newToken.earnAmount);
      const hasAny =
        newToken.tokenType.trim() || newToken.spendAmount || newToken.earnAmount || newToken.tokenId;
      if (hasAny) {
        if (!newToken.tokenType.trim() || !spendNum || spendNum <= 0 || !earnNum || earnNum <= 0) {
          return { error: 'Complete new token rule or cancel before saving.' };
        }
        rules = [
          ...rules,
          {
            id: generateLocalId(),
            tokenId: newToken.tokenId?.trim() || null,
            tokenType: newToken.tokenType.trim(),
            spendAmount: spendNum,
            earnAmount: earnNum,
            createdAt: new Date().toISOString()
          }
        ];
      }
    }

    const rewards = {
      ...base,
      voucherPool: pool,
      tokenRules: rules,
      voucherRotationEnabled: base.voucherRotationEnabled
    };
    return { rewards, error: null };
  }, [
    editingVoucherId,
    editingVoucher,
    editingTokenId,
    editingToken,
    isAddingVoucher,
    newVoucher,
    isAddingToken,
    newToken,
    voucherSchemas
  ]);

  const handleSave = async () => {
    const built = buildRewardsForPersist();
    if (built.error) {
      setMessage({ type: 'error', text: built.error });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await onUpdate({
        rewards: {
          ...built.rewards,
          voucherRotationEnabled: built.rewards.voucherRotationEnabled
        }
      });
      setFormData(built.rewards);
      setEditingVoucherId(null);
      setEditingVoucher({ name: '', schemaId: '', inventory: '' });
      setEditingTokenId(null);
      setEditingToken({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
      setNewVoucher({ name: '', schemaId: '', inventory: '' });
      setNewToken({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
      setIsAddingVoucher(false);
      setIsAddingToken(false);
      setMessage({ type: 'success', text: 'Reward settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddVoucher = () => {
    if (!newVoucher.name.trim() || !newVoucher.schemaId.trim()) return;
    if (newVoucher.inventory !== '' && parseInt(newVoucher.inventory, 10) <= 0) return;
    const selNew = findVoucherSchema(voucherSchemas, newVoucher.schemaId);
    const duplicate = selNew
      ? isVoucherAlreadyInPool(selNew, formData.voucherPool, null)
      : (formData.voucherPool || []).some((v) => normId(v.schemaId) === normId(newVoucher.schemaId));
    if (duplicate) {
      setMessage({ type: 'error', text: 'This voucher is already in the pool.' });
      return;
    }
    if (voucherCampaignIsInactive(selNew)) {
      setMessage({ type: 'error', text: "This voucher's campaign is no longer active and cannot be added as a reward." });
      return;
    }
    setFormData((prev) => {
      const pool = prev.voucherPool || [];
      const invParsed = parseInt(newVoucher.inventory, 10);
      const inv =
        newVoucher.inventory !== '' && !Number.isNaN(invParsed) && invParsed > 0 ? invParsed : null;
      const row = {
        id: generateLocalId(),
        name: newVoucher.name.trim(),
        schemaId: newVoucher.schemaId.trim(),
        inventory: inv,
        issued: 0,
        order: pool.length + 1,
        createdAt: new Date().toISOString(),
        ...vioSnapshotFields(selNew)
      };
      return { ...prev, voucherPool: [...pool, row] };
    });
    setNewVoucher({ name: '', schemaId: '', inventory: '' });
    setIsAddingVoucher(false);
    setEditingVoucherId(null);
  };

  const handleAddTokenRule = () => {
    const spendNum = parseFloat(newToken.spendAmount);
    const earnNum = parseFloat(newToken.earnAmount);
    if (!newToken.tokenType.trim() || !spendNum || spendNum <= 0 || !earnNum || earnNum <= 0) return;
    setFormData((prev) => ({
      ...prev,
      tokenRules: [
        {
          id: generateLocalId(),
          tokenId: newToken.tokenId?.trim() || null,
          tokenType: newToken.tokenType.trim(),
          spendAmount: spendNum,
          earnAmount: earnNum,
          createdAt: new Date().toISOString()
        }
      ]
    }));
    setNewToken({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
    setIsAddingToken(false);
    setEditingTokenId(null);
  };

  const handleStartEditVoucher = (voucher) => {
    setEditingVoucherId(voucher.id);
    setEditingVoucher({
      name: voucher.name,
      schemaId: voucher.schemaId,
      inventory: voucher.inventory || ''
    });
    setIsAddingVoucher(false);
    setEditingTokenId(null);
  };

  const handleCancelEditVoucher = () => {
    setEditingVoucherId(null);
    setEditingVoucher({ name: '', schemaId: '', inventory: '' });
  };

  const handleSaveEditVoucher = () => {
    if (!editingVoucher.name.trim() || !editingVoucher.schemaId.trim()) return;
    if (editingVoucher.inventory !== '' && parseInt(editingVoucher.inventory, 10) <= 0) return;
    const selEdit = findVoucherSchema(voucherSchemas, editingVoucher.schemaId);
    const duplicate = selEdit
      ? isVoucherAlreadyInPool(selEdit, formData.voucherPool, editingVoucherId)
      : (formData.voucherPool || []).some((v) => v.id !== editingVoucherId && normId(v.schemaId) === normId(editingVoucher.schemaId));
    if (duplicate) {
      setMessage({ type: 'error', text: 'This voucher is already in the pool.' });
      return;
    }
    if (voucherCampaignIsInactive(selEdit)) {
      setMessage({ type: 'error', text: "This voucher's campaign is no longer active and cannot be used as a reward." });
      return;
    }
    const invParsed = parseInt(editingVoucher.inventory, 10);
    const inv =
      editingVoucher.inventory !== '' && !Number.isNaN(invParsed) && invParsed > 0 ? invParsed : null;
    setFormData((prev) => ({
      ...prev,
      voucherPool: (prev.voucherPool || []).map((v) =>
        v.id === editingVoucherId
          ? {
              ...v,
              name: editingVoucher.name.trim(),
              schemaId: editingVoucher.schemaId.trim(),
              inventory: inv,
              ...vioSnapshotFields(selEdit)
            }
          : v
      )
    }));
    setEditingVoucherId(null);
    setEditingVoucher({ name: '', schemaId: '', inventory: '' });
  };

  const handleStartEditToken = (rule) => {
    setEditingTokenId(rule.id);
    setEditingToken({
      tokenId: rule.tokenId || '',
      tokenType: rule.tokenType,
      spendAmount: rule.spendAmount,
      earnAmount: rule.earnAmount
    });
    setIsAddingToken(false);
    setEditingVoucherId(null);
  };

  const handleCancelEditToken = () => {
    setEditingTokenId(null);
    setEditingToken({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
  };

  const handleSaveEditToken = () => {
    const spendNum = parseFloat(editingToken.spendAmount);
    const earnNum = parseFloat(editingToken.earnAmount);
    if (!editingToken.tokenType.trim() || !spendNum || spendNum <= 0 || !earnNum || earnNum <= 0) return;
    setFormData((prev) => ({
      ...prev,
      tokenRules: (prev.tokenRules || []).map((r) =>
        r.id === editingTokenId
          ? {
              ...r,
              tokenId: editingToken.tokenId?.trim() || null,
              tokenType: editingToken.tokenType.trim(),
              spendAmount: spendNum,
              earnAmount: earnNum
            }
          : r
      )
    }));
    setEditingTokenId(null);
    setEditingToken({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
  };

  const handleRemoveVoucherFromPool = (id) => {
    setFormData((prev) => ({
      ...prev,
      voucherPool: (prev.voucherPool || []).filter((v) => v.id !== id)
    }));
  };

  const handleRemoveTokenRule = (id) => {
    setFormData((prev) => ({
      ...prev,
      tokenRules: (prev.tokenRules || []).filter((r) => r.id !== id)
    }));
  };

  useImperativeHandle(ref, () => ({
    saveStep: async () => {
      const built = buildRewardsForPersist();
      if (built.error) {
        return { ok: false, error: built.error };
      }
      setSaving(true);
      setMessage(null);
      try {
        await onUpdate({
          rewards: {
            ...built.rewards,
            voucherRotationEnabled: built.rewards.voucherRotationEnabled
          }
        });
        setFormData(built.rewards);
        setEditingVoucherId(null);
        setEditingVoucher({ name: '', schemaId: '', inventory: '' });
        setEditingTokenId(null);
        setEditingToken({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
        setNewVoucher({ name: '', schemaId: '', inventory: '' });
        setNewToken({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
        setIsAddingVoucher(false);
        setIsAddingToken(false);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: e?.message || 'Failed to save reward rules.' };
      } finally {
        setSaving(false);
      }
    }
  }), [buildRewardsForPersist, onUpdate]);

  return (
    <div className="tab-panel">
      <div className="tab-header">
        <h2>Reward Rules</h2>
        <p>Configure reward types and issuance logic for approved claims.</p>
      </div>

      <div className="rules-form">
        {/* Reward Types */}
        <div className="form-section">
          <div className="form-section-title">Reward Type (Select One)</div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 0.75rem 0' }}>
            Choose either Voucher or Token as the reward type. Only one can be active at a time.
          </p>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="rewardType"
                checked={formData.types.includes('VOUCHER')}
                onChange={() => handleTypeToggle('VOUCHER')}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '500' }}>Vouchers</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="rewardType"
                checked={formData.types.includes('TOKEN')}
                onChange={() => handleTypeToggle('TOKEN')}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '500' }}>Tokens</span>
            </label>
          </div>
        </div>

        {/* Voucher Pool */}
        {formData.types.includes('VOUCHER') && (
          <div className="form-section">
            <div className="form-section-title">Voucher Pool</div>
            <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
              Vouchers are issued in rotation order. After the last voucher, it cycles back to the first.
            </p>

            <div className="items-list">
              {(formData.voucherPool || []).length === 0 ? (
                <div className="empty-state-mini">
                  <p>No vouchers in pool. Add vouchers to enable rewards.</p>
                </div>
              ) : (
                (formData.voucherPool || []).map((voucher, index) => {
                  if (editingVoucherId === voucher.id) {
                    return (
                    <div key={voucher.id} className="item-card" style={{ background: '#f8fafc', flexDirection: 'column', gap: '0.75rem', alignItems: 'stretch' }}>
                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        {voucherSchemas && voucherSchemas.length > 0 ? (
                          <select
                            className="rules-select"
                            value={editingVoucher.schemaId}
                            onChange={(e) => {
                              const selected = findVoucherSchema(voucherSchemas, e.target.value);
                              setEditingVoucher({ 
                                ...editingVoucher, 
                                schemaId: e.target.value,
                                name: selected?.name || editingVoucher.name
                              });
                            }}
                            style={{ flex: 2, minWidth: '200px', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                            autoFocus
                          >
                            <option value="">Select voucher from VIO...</option>
                            {voucherSchemas.map(schema => {
                              const expired = voucherSchemaIsExpired(schema);
                              const inactive = voucherCampaignIsInactive(schema);
                              const alreadyAdded = isVoucherAlreadyInPool(schema, formData.voucherPool, editingVoucherId);
                              const baseOk = isVoucherSelectable(schema);
                              const selectable = baseOk && !expired && !inactive && !alreadyAdded;
                              const stock = voucherStockDisplay(schema);
                              const perUser = maxClaimsPerUserLabel(schema);
                              let reason = '';
                              if (alreadyAdded) reason = ' — Already in pool';
                              else if (inactive) reason = ' — Campaign inactive';
                              else if (expired) reason = ' — Expired';
                              else if (!baseOk) reason = hasPerUserClaimLimit(schema) ? ' — Has per-user limit' : ' — Sold out';
                              return (
                                <option key={schema.id} value={schema.id} disabled={!selectable}>
                                  {schema.name} ({stock.text}) [{perUser}]{reason}
                                </option>
                              );
                            })}
                          </select>
                        ) : (
                          <>
                            <input
                              type="text"
                              placeholder="Voucher name"
                              value={editingVoucher.name}
                              onChange={(e) => setEditingVoucher({ ...editingVoucher, name: e.target.value })}
                              style={{ flex: 1, minWidth: '150px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                              autoFocus
                            />
                            <input
                              type="text"
                              placeholder="Campaign Voucher Schema ID"
                              value={editingVoucher.schemaId}
                              onChange={(e) => setEditingVoucher({ ...editingVoucher, schemaId: e.target.value })}
                              style={{ flex: 1, minWidth: '150px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                            />
                          </>
                        )}
                        {(() => {
                          const selectedSchema = findVoucherSchema(voucherSchemas, editingVoucher.schemaId);
                          const maxQuantity = safePositiveBalance(voucherRemainingIssuableCount(selectedSchema));
                          return (
                            <input
                              type="number"
                              min="1"
                              max={maxQuantity}
                              placeholder={maxQuantity ? `Max ${maxQuantity}` : "Pool limit (optional)"}
                              value={editingVoucher.inventory}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (maxQuantity && parseInt(val, 10) > maxQuantity) {
                                  setEditingVoucher({ ...editingVoucher, inventory: maxQuantity.toString() });
                                } else {
                                  setEditingVoucher({ ...editingVoucher, inventory: val });
                                }
                              }}
                              style={{ width: '140px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                              title={maxQuantity ? `Max ${maxQuantity} remaining in VIO` : "Max number to issue from this pool (leave empty for unlimited)"}
                            />
                          );
                        })()}
                      </div>
                      {(() => {
                        const sel = findVoucherSchema(voucherSchemas, editingVoucher.schemaId);
                        return sel ? <RewardSelectionAlertsVoucher schema={sel} /> : null;
                      })()}
                      {!voucherSchemas?.length && (
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                          ⚠️ Could not load VIO vouchers. Enter schema ID manually or check authentication.
                        </div>
                      )}
                      {renderAlerts(getVoucherAlerts({
                        ...voucher,
                        schemaId: editingVoucher.schemaId,
                        inventory: editingVoucher.inventory || voucher.inventory,
                      }, voucherSchemas))}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                        <button className="btn-action secondary" onClick={handleCancelEditVoucher}>
                          Cancel
                        </button>
                        <button 
                          className="btn-action primary" 
                          onClick={handleSaveEditVoucher}
                          disabled={!editingVoucher.schemaId || !editingVoucher.name}
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                    );
                  }
                  const sel = findVoucherSchema(voucherSchemas, voucher.schemaId);
                  const rowState = getConfiguredVoucherRowState(sel, voucher, voucherSchemas);
                  const availableQty = voucherAvailableQuantityFromApi(sel);
                  const configuredAmount = voucherConfiguredPoolAmountText(voucher);
                  // Name + image: live API list row when matched; else snapshot written from the same list row at save.
                  const displayName =
                    sel != null ? sel.name : (voucher.vioDisplayName || voucher.name || 'Voucher');
                  const rowImageUrl = sel != null ? sel.imageUrl : voucher.vioImageUrl;
                  return (
                        <div
                          key={voucher.id}
                          className={`item-card item-card--reward-voucher${rowState.muted ? ' item-card--reward-unavailable' : ''}`}
                        >
                          <div className={`item-info${rowState.muted ? ' item-info--reward-muted' : ''}`}>
                            <RewardListThumb
                              imageUrl={rowImageUrl}
                              alt={displayName || 'Voucher'}
                              fallback={`#${voucher.order || index + 1}`}
                              fallbackStyle={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
                              muted={rowState.muted}
                            />
                            <div className="item-details">
                              <h4 className="item-details__reward-title">
                                <span className="item-details__reward-name">{displayName}</span>
                                {rowState.badge ? (
                                  <span
                                    className={`reward-status-badge${
                                      rowState.badge === 'Expired'
                                        ? ' reward-status-badge--expired'
                                        : rowState.badge === 'Unavailable' || rowState.badge === 'Inactive'
                                          ? ' reward-status-badge--unavailable'
                                          : ' reward-status-badge--stock'
                                    }`}
                                  >
                                    {rowState.badge}
                                  </span>
                                ) : null}
                              </h4>
                              {availableQty != null ? (
                                <span>Available quantity: {availableQty}</span>
                              ) : null}
                              <span title="Pool issuance limit you configured for this reward">
                                Configured amount:{' '}
                                <span className="reward-voucher-configured-value">{configuredAmount}</span>
                              </span>
                              {sel ? <RewardSelectionAlertsVoucher schema={sel} /> : null}
                            </div>
                          </div>
                          <div className="item-actions">
                            <button
                              className="btn-icon-sm"
                              onClick={() => handleStartEditVoucher(voucher)}
                              title="Edit"
                              style={{ marginRight: '0.5rem' }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                            <button
                              className="btn-icon-sm delete"
                              onClick={() => handleRemoveVoucherFromPool(voucher.id)}
                              title="Remove"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 6h18"></path>
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                  );
                })
              )}

              {isAddingVoucher && !editingVoucherId ? (
                <div className="item-card" style={{ background: '#f8fafc', flexDirection: 'column', gap: '0.75rem', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {voucherSchemas && voucherSchemas.length > 0 ? (
                      <select
                        className="rules-select"
                        value={newVoucher.schemaId}
                        onChange={(e) => {
                          const selected = findVoucherSchema(voucherSchemas, e.target.value);
                          setNewVoucher({ 
                            ...newVoucher, 
                            schemaId: e.target.value,
                            name: selected?.name || ''
                          });
                        }}
                        style={{ flex: 2, minWidth: '200px', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                        autoFocus
                      >
                        <option value="">Select voucher from VIO...</option>
                        {voucherSchemas.map(schema => {
                          const expired = voucherSchemaIsExpired(schema);
                          const inactive = voucherCampaignIsInactive(schema);
                          const alreadyAdded = isVoucherAlreadyInPool(schema, formData.voucherPool, null);
                          const baseOk = isVoucherSelectable(schema);
                          const selectable = baseOk && !expired && !inactive && !alreadyAdded;
                          const stock = voucherStockDisplay(schema);
                          const perUser = maxClaimsPerUserLabel(schema);
                          let reason = '';
                          if (alreadyAdded) reason = ' — Already in pool';
                          else if (inactive) reason = ' — Campaign inactive';
                          else if (expired) reason = ' — Expired';
                          else if (!baseOk) reason = hasPerUserClaimLimit(schema) ? ' — Has per-user limit' : ' — Sold out';
                          return (
                            <option key={schema.id} value={schema.id} disabled={!selectable}>
                              {schema.name} ({stock.text}) [{perUser}]{reason}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <>
                        <input
                          type="text"
                          placeholder="Voucher name"
                          value={newVoucher.name}
                          onChange={(e) => setNewVoucher({ ...newVoucher, name: e.target.value })}
                          style={{ flex: 1, minWidth: '150px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                          autoFocus
                        />
                        <input
                          type="text"
                          placeholder="Campaign Voucher Schema ID"
                          value={newVoucher.schemaId}
                          onChange={(e) => setNewVoucher({ ...newVoucher, schemaId: e.target.value })}
                          style={{ flex: 1, minWidth: '150px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                        />
                      </>
                    )}
                    {(() => {
                      const selectedSchema = findVoucherSchema(voucherSchemas, newVoucher.schemaId);
                      const maxQuantity = safePositiveBalance(voucherRemainingIssuableCount(selectedSchema));
                      return (
                        <input
                          type="number"
                          min="1"
                          max={maxQuantity}
                          placeholder={maxQuantity ? `Max ${maxQuantity}` : "Pool limit (optional)"}
                          value={newVoucher.inventory}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (maxQuantity && parseInt(val, 10) > maxQuantity) {
                              setNewVoucher({ ...newVoucher, inventory: maxQuantity.toString() });
                            } else {
                              setNewVoucher({ ...newVoucher, inventory: val });
                            }
                          }}
                          style={{ width: '140px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                          title={maxQuantity ? `Max ${maxQuantity} remaining in VIO` : "Max number to issue from this pool (leave empty for unlimited)"}
                        />
                      );
                    })()}
                  </div>
                  {(() => {
                    const sel = findVoucherSchema(voucherSchemas, newVoucher.schemaId);
                    return sel ? <RewardSelectionAlertsVoucher schema={sel} /> : null;
                  })()}
                  {!voucherSchemas?.length && (
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                      ⚠️ Could not load VIO vouchers. Enter schema ID manually or check authentication.
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                    <button className="btn-action secondary" onClick={() => setIsAddingVoucher(false)}>
                      Cancel
                    </button>
                    <button 
                      className="btn-action primary" 
                      onClick={handleAddVoucher}
                      disabled={!newVoucher.schemaId || !newVoucher.name}
                    >
                      Add Voucher
                    </button>
                  </div>
                </div>
              ) : !editingVoucherId ? (
                <button className="add-item-btn" onClick={() => {
                  setIsAddingVoucher(true);
                  setEditingVoucherId(null);
                }}>
                  <span>+</span> Add Voucher to Pool
                </button>
              ) : null}
            </div>
          </div>
        )}

        {/* Token Rules */}
        {formData.types.includes('TOKEN') && (
          <div className="form-section">
            <div className="form-section-title">Token Conversion Rule</div>
            <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
              Select one token type and define the spend-to-token conversion ratio. Only one token type can be configured.
            </p>
            
            <div className="items-list">
              {(formData.tokenRules || []).length === 0 ? (
                <div className="empty-state-mini">
                  <p>No token configured. Add a token rule to enable token rewards.</p>
                </div>
              ) : (
                (formData.tokenRules || []).map((rule) => {
                  if (editingTokenId === rule.id) {
                    return (
                    <div key={rule.id} className="item-card" style={{ background: '#f8fafc', flexDirection: 'column', gap: '0.75rem', alignItems: 'stretch' }}>
                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        {tokenList && tokenList.length > 0 ? (
                          <select
                            className="rules-select"
                            value={editingToken.tokenId}
                            onChange={(e) => {
                              const selected = tokenList.find(t => t.id === e.target.value);
                              setEditingToken({ 
                                ...editingToken, 
                                tokenId: e.target.value,
                                tokenType: selected ? `${selected.name} (${selected.symbol})` : editingToken.tokenType
                              });
                            }}
                            style={{ flex: 2, minWidth: '180px', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                            autoFocus
                          >
                            <option value="">Select token from VIO...</option>
                            {tokenList.map(token => {
                              const bal = safePositiveBalance(token.balance);
                              const balLabel = bal === undefined ? 'Unlimited' : bal.toLocaleString();
                              const unavailable = bal !== undefined && bal <= 0;
                              return (
                                <option key={token.id} value={token.id} disabled={unavailable}>
                                  {token.name} ({token.symbol}) - Balance: {balLabel}{unavailable ? ' (Unavailable)' : ''}
                                </option>
                              );
                            })}
                          </select>
                        ) : (
                          <input
                            type="text"
                            placeholder="Token type (e.g., VIO Point)"
                            value={editingToken.tokenType}
                            onChange={(e) => setEditingToken({ ...editingToken, tokenType: e.target.value })}
                            style={{ flex: 2, minWidth: '150px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                            autoFocus
                          />
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span style={{ color: '#64748b' }}>RM</span>
                          <input
                            type="number"
                            min="1"
                            placeholder="Spend"
                            value={editingToken.spendAmount}
                            onChange={(e) => setEditingToken({ ...editingToken, spendAmount: e.target.value })}
                            style={{ width: '80px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                          />
                        </div>
                        <span style={{ color: '#64748b', alignSelf: 'center' }}>→</span>
                        {(() => {
                          const selectedToken = findTokenInList(tokenList, editingToken.tokenId);
                          const maxBalance = selectedToken ? safePositiveBalance(selectedToken.balance) : undefined;
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <input
                                type="number"
                                min="1"
                                max={maxBalance}
                                placeholder="Earn"
                                value={editingToken.earnAmount}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (maxBalance && parseFloat(val) > maxBalance) {
                                    setEditingToken({ ...editingToken, earnAmount: maxBalance.toString() });
                                  } else {
                                    setEditingToken({ ...editingToken, earnAmount: val });
                                  }
                                }}
                                style={{ width: '80px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                                title={maxBalance ? `Max ${maxBalance.toLocaleString()} available` : ''}
                              />
                              <span style={{ color: '#64748b' }}>Token(s)</span>
                            </div>
                          );
                        })()}
                      </div>
                      {(() => {
                        const t = findTokenInList(tokenList, editingToken.tokenId);
                        return t ? <RewardSelectionAlertsToken token={t} /> : null;
                      })()}
                      {!tokenList?.length && (
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                          ⚠️ Could not load VIO tokens. Enter token type manually or check authentication.
                        </div>
                      )}
                      {renderAlerts(getTokenAlerts({
                        ...rule,
                        tokenId: editingToken.tokenId,
                      }, tokenList))}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                        <button className="btn-action secondary" onClick={handleCancelEditToken}>
                          Cancel
                        </button>
                        <button 
                          className="btn-action primary" 
                          onClick={handleSaveEditToken}
                          disabled={!editingToken.tokenType || !editingToken.spendAmount || parseFloat(editingToken.spendAmount) <= 0 || !editingToken.earnAmount || parseFloat(editingToken.earnAmount) <= 0}
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                    );
                  }
                  const t = rule.tokenId ? findTokenInList(tokenList, rule.tokenId) : null;
                  const rowState = getConfiguredTokenRowState(t);
                  const availableQty = tokenAvailableQuantityFromApi(t);
                  return (
                        <div
                          key={rule.id}
                          className={`item-card item-card--reward-voucher${rowState.muted ? ' item-card--reward-unavailable' : ''}`}
                        >
                          <div className={`item-info${rowState.muted ? ' item-info--reward-muted' : ''}`}>
                            <RewardListThumb
                              imageUrl={t?.logoUrl}
                              alt={rule.tokenType || 'Token'}
                              fallback="🪙"
                              fallbackStyle={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                              muted={rowState.muted}
                            />
                            <div className="item-details">
                              <h4 className="item-details__reward-title">
                                <span className="item-details__reward-name">{rule.tokenType}</span>
                                {rowState.badge ? (
                                  <span className="reward-status-badge reward-status-badge--stock">{rowState.badge}</span>
                                ) : null}
                              </h4>
                              {availableQty != null ? (
                                <span>Available quantity: {availableQty}</span>
                              ) : null}
                              <span>
                                Spend RM{rule.spendAmount} → Earn {rule.earnAmount} Token(s)
                              </span>
                              {t ? <RewardSelectionAlertsToken token={t} /> : null}
                            </div>
                          </div>
                          <div className="item-actions">
                            <button
                              className="btn-icon-sm"
                              onClick={() => handleStartEditToken(rule)}
                              title="Edit"
                              style={{ marginRight: '0.5rem' }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                            <button
                              className="btn-icon-sm delete"
                              onClick={() => handleRemoveTokenRule(rule.id)}
                              title="Remove"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 6h18"></path>
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                  );
                })
              )}

              {isAddingToken && !editingTokenId && (formData.tokenRules || []).length === 0 ? (
                <div className="item-card" style={{ background: '#f8fafc', flexDirection: 'column', gap: '0.75rem', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    {tokenList && tokenList.length > 0 ? (
                      <select
                        className="rules-select"
                        value={newToken.tokenId}
                        onChange={(e) => {
                          const selected = tokenList.find(t => t.id === e.target.value);
                          setNewToken({ 
                            ...newToken, 
                            tokenId: e.target.value,
                            tokenType: selected ? `${selected.name} (${selected.symbol})` : ''
                          });
                        }}
                        style={{ flex: 2, minWidth: '180px', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                        autoFocus
                      >
                        <option value="">Select token from VIO...</option>
                        {tokenList.map(token => {
                          const bal = safePositiveBalance(token.balance);
                          const balLabel = bal === undefined ? 'Unlimited' : bal.toLocaleString();
                          const unavailable = bal !== undefined && bal <= 0;
                          return (
                            <option key={token.id} value={token.id} disabled={unavailable}>
                              {token.name} ({token.symbol}) - Balance: {balLabel}{unavailable ? ' (Unavailable)' : ''}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <input
                        type="text"
                        placeholder="Token type (e.g., VIO Point)"
                        value={newToken.tokenType}
                        onChange={(e) => setNewToken({ ...newToken, tokenType: e.target.value })}
                        style={{ flex: 2, minWidth: '150px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                        autoFocus
                      />
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <span style={{ color: '#64748b' }}>RM</span>
                      <input
                        type="number"
                        min="1"
                        placeholder="Spend"
                        value={newToken.spendAmount}
                        onChange={(e) => setNewToken({ ...newToken, spendAmount: e.target.value })}
                        style={{ width: '80px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                      />
                    </div>
                    <span style={{ color: '#64748b', alignSelf: 'center' }}>→</span>
                    {(() => {
                      const selectedToken = findTokenInList(tokenList, newToken.tokenId);
                      const maxBalance = selectedToken ? safePositiveBalance(selectedToken.balance) : undefined;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <input
                            type="number"
                            min="1"
                            max={maxBalance}
                            placeholder="Earn"
                            value={newToken.earnAmount}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (maxBalance && parseFloat(val) > maxBalance) {
                                setNewToken({ ...newToken, earnAmount: maxBalance.toString() });
                              } else {
                                setNewToken({ ...newToken, earnAmount: val });
                              }
                            }}
                            style={{ width: '80px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                            title={maxBalance ? `Max ${maxBalance.toLocaleString()} available` : ''}
                          />
                          <span style={{ color: '#64748b' }}>Token(s)</span>
                        </div>
                      );
                    })()}
                  </div>
                  {(() => {
                    const t = findTokenInList(tokenList, newToken.tokenId);
                    return t ? <RewardSelectionAlertsToken token={t} /> : null;
                  })()}
                  {!tokenList?.length && (
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                      ⚠️ Could not load VIO tokens. Enter token type manually or check authentication.
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                    <button className="btn-action secondary" onClick={() => setIsAddingToken(false)}>
                      Cancel
                    </button>
                    <button 
                      className="btn-action primary" 
                      onClick={handleAddTokenRule}
                      disabled={!newToken.tokenType || !newToken.spendAmount || parseFloat(newToken.spendAmount) <= 0 || !newToken.earnAmount || parseFloat(newToken.earnAmount) <= 0}
                    >
                      Add Rule
                    </button>
                  </div>
                </div>
              ) : !editingTokenId && (formData.tokenRules || []).length === 0 ? (
                <button className="add-item-btn" onClick={() => {
                  setIsAddingToken(true);
                  setEditingTokenId(null);
                }}>
                  <span>+</span> Add Token Rule
                </button>
              ) : null}
            </div>
          </div>
        )}

        {message && (
          <div className={`status-message ${message.type}`}>
            {message.type === 'success' ? '✓' : '✕'} {message.text}
          </div>
        )}

        {!hideWizardSaveBar && (
          <div className="save-bar">
            <button 
              className="btn-save" 
              onClick={handleSave}
              disabled={saving || !isDirty}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default RewardsTab;
